/**
 * audio/sfx-library.js — the name → recipe registry.
 *
 * Two naming schemes have to work at once:
 *
 *   - the descriptive recipe names this engine defines (`throw-whoosh`,
 *     `score-chime`, `prize-tier`);
 *   - the GDD 11.2 event names already called from `stalls/` and `ui/`
 *     (`throw-release`, `score-popup`, `prize-earned`).
 *
 * The second set are aliases onto the first, so no file outside `src/audio/`
 * has to change. Aliases are also generous about the names a stall agent might
 * reach for unprompted — `hit`, `pop`, `cheer`, `click` all resolve.
 *
 * Registry entry: `{ fn, dur, bus, dedupe }`
 *   fn      recipe(ctx, dest, opts, t0) → absolute end time
 *   dur     nominal seconds, used for voice budgeting and offline render length
 *   bus     which output bus it belongs on
 *   dedupe  ms window in which a repeat of the same name is folded into the
 *           first — see `audio.play` for why that matters
 *
 * @module audio/sfx-library
 */

import * as core from './recipes-core.js';
import * as impact from './recipes-impact.js';
import * as reward from './recipes-reward.js';

/**
 * @typedef {(ctx: BaseAudioContext, dest: AudioNode, opts: object, t0: number) => number} Recipe
 */

/**
 * Material → recipe. `impact-hit` routes through this so a stall can emit one
 * event name and still get the right material identity.
 */
const MATERIALS = Object.freeze({
  can: 'can-clatter',
  tin: 'can-clatter',
  metal: 'can-clatter',
  coconut: 'coconut-thunk',
  wood: 'plate-knock',
  plate: 'plate-knock',
  crockery: 'plate-knock',
  board: 'plate-knock',
  rim: 'rim-clang',
  hoop: 'rim-clang',
  ring: 'rim-clang',
  bottle: 'rim-clang',
  dart: 'dart-thunk',
  cork: 'dart-thunk',
  mole: 'mole-bonk',
  balloon: 'balloon-pop',
  water: 'splash',
});

/** Stall → default impact material, when a stall does not name one. */
const STALL_MATERIAL = Object.freeze({
  'tin-can-alley': 'can',
  'coconut-shy': 'coconut',
  'ring-toss': 'rim',
  'ping-pong-goldfish': 'water',
  'basketball-shoot': 'rim',
  'skee-ball': 'wood',
  'duck-gallery': 'wood',
  'dart-balloons': 'dart',
  'high-striker': 'wood',
  'whack-a-mole': 'mole',
  'hook-a-duck': 'water',
  'coin-pusher': 'wood',
});

/**
 * Generic impact — resolves the material, then plays that recipe.
 * @type {Recipe}
 */
function impactHit(ctx, dest, o = {}, t0 = 0) {
  const named = o.material || STALL_MATERIAL[o.stallId] || 'can';
  const target = MATERIALS[String(named).toLowerCase()] || 'can-clatter';
  const rec = RECIPES[target];
  return rec.fn(ctx, dest, o, t0);
}

/** @type {Record<string, { fn: Recipe, dur: number, bus: string, dedupe: number }>} */
export const RECIPES = {
  // ── Handling and money ────────────────────────────────────────────────────
  'coin-insert': { fn: core.coinInsert, dur: 0.5, bus: 'sfx', dedupe: 160 },
  grab: { fn: core.grabClick, dur: 0.12, bus: 'sfx', dedupe: 50 },
  release: { fn: core.releaseClick, dur: 0.08, bus: 'sfx', dedupe: 50 },
  'throw-whoosh': { fn: core.throwWhoosh, dur: 0.45, bus: 'sfx', dedupe: 90 },

  // ── Impacts ───────────────────────────────────────────────────────────────
  'impact-hit': { fn: impactHit, dur: 0.6, bus: 'sfx', dedupe: 0 },
  'can-clatter': { fn: impact.canClatter, dur: 0.65, bus: 'sfx', dedupe: 0 },
  'coconut-thunk': { fn: impact.coconutThunk, dur: 0.4, bus: 'sfx', dedupe: 0 },
  'plate-knock': { fn: impact.woodKnock, dur: 0.36, bus: 'sfx', dedupe: 0 },
  'rim-clang': { fn: impact.rimClang, dur: 0.7, bus: 'sfx', dedupe: 0 },
  'dart-thunk': { fn: impact.dartThunk, dur: 0.24, bus: 'sfx', dedupe: 0 },
  'mole-bonk': { fn: impact.moleBonk, dur: 0.34, bus: 'sfx', dedupe: 0 },
  'impact-miss': { fn: impact.impactMiss, dur: 0.3, bus: 'sfx', dedupe: 0 },
  'balloon-pop': { fn: impact.balloonPop, dur: 0.46, bus: 'sfx', dedupe: 0 },
  splash: { fn: impact.splash, dur: 0.8, bus: 'sfx', dedupe: 60 },
  bubbles: { fn: impact.bubbles, dur: 0.6, bus: 'sfx', dedupe: 0 },

  // ── Scoring and payout ────────────────────────────────────────────────────
  'score-chime': { fn: reward.scoreChime, dur: 0.6, bus: 'sfx', dedupe: 70 },
  'ticket-tally': { fn: reward.ticketTally, dur: 1.3, bus: 'sfx', dedupe: 400 },
  'token-award': { fn: reward.tokenAward, dur: 0.7, bus: 'sfx', dedupe: 300 },

  // ── Prizes and celebration ────────────────────────────────────────────────
  'prize-tier': { fn: reward.prizeTier, dur: 1.3, bus: 'sfx', dedupe: 260 },
  'set-piece': { fn: reward.setPieceSting, dur: 1.0, bus: 'sfx', dedupe: 300 },
  'set-complete': { fn: reward.setComplete, dur: 2.4, bus: 'sfx', dedupe: 600 },
  'bell-ring': { fn: reward.bellRing, dur: 3.3, bus: 'sfx', dedupe: 500 },
  'crowd-cheer': { fn: reward.crowdCheer, dur: 2.0, bus: 'sfx', dedupe: 300 },
  'golden-duck': { fn: reward.goldenDuck, dur: 1.3, bus: 'sfx', dedupe: 400 },
  'fish-win': { fn: reward.fishWin, dur: 0.9, bus: 'sfx', dedupe: 300 },
  'fish-name': { fn: reward.fishName, dur: 0.8, bus: 'sfx', dedupe: 300 },

  // ── The goose, movement and interface ─────────────────────────────────────
  honk: { fn: core.honk, dur: 0.55, bus: 'sfx', dedupe: 80 },
  'waddle-step': { fn: core.waddleStep, dur: 0.16, bus: 'sfx', dedupe: 0 },
  teleport: { fn: core.teleportBlink, dur: 0.5, bus: 'sfx', dedupe: 220 },
  'ui-click': { fn: core.uiClick, dur: 0.08, bus: 'sfx', dedupe: 40 },
  'toast-good': { fn: core.toastGood, dur: 0.4, bus: 'sfx', dedupe: 160 },
  'toast-bad': { fn: core.toastBad, dur: 0.44, bus: 'sfx', dedupe: 160 },
  'insufficient-tickets': { fn: core.insufficientTickets, dur: 0.7, bus: 'sfx', dedupe: 400 },

  // ── Stall ambience ────────────────────────────────────────────────────────
  'machine-hum': { fn: impact.machineHum, dur: 2.0, bus: 'ambient', dedupe: 500 },
};

/**
 * Alternate names that resolve onto a recipe. The first block is GDD 11.2 and
 * is load-bearing — `stalls/` and `ui/` already call those.
 */
export const ALIASES = Object.freeze({
  // GDD 11.2 names in use elsewhere in the codebase
  'goose-waddle': 'waddle-step',
  'throw-release': 'throw-whoosh',
  'score-popup': 'score-chime',
  'prize-earned': 'prize-tier',
  'water-splash': 'splash',
  'ambient-carnival': 'crowd-cheer',
  'mole-hit': 'mole-bonk',

  // Shorthands a stall might reasonably reach for
  throw: 'throw-whoosh',
  whoosh: 'throw-whoosh',
  hit: 'impact-hit',
  impact: 'impact-hit',
  miss: 'impact-miss',
  pop: 'balloon-pop',
  water: 'splash',
  score: 'score-chime',
  chime: 'score-chime',
  tally: 'ticket-tally',
  'ticket-rattle': 'ticket-tally',
  token: 'token-award',
  'tokens-awarded': 'token-award',
  prize: 'prize-tier',
  'set-piece-earned': 'set-piece',
  'set-piece-sting': 'set-piece',
  'set-completed': 'set-complete',
  'cosmetic-unlock': 'set-complete',
  bell: 'bell-ring',
  'high-striker-bell': 'bell-ring',
  cheer: 'crowd-cheer',
  'goose-honk': 'honk',
  step: 'waddle-step',
  footstep: 'waddle-step',
  blink: 'teleport',
  'teleport-blink': 'teleport',
  click: 'ui-click',
  ui: 'ui-click',
  button: 'ui-click',
  'ui-button': 'ui-click',
  toast: 'toast-good',
  'no-tickets': 'insufficient-tickets',
  insufficient: 'insufficient-tickets',
  'cant-afford': 'insufficient-tickets',
  golden: 'golden-duck',
  'duck-golden': 'golden-duck',
  fish: 'fish-win',
  'fish-prize': 'fish-win',
  'fish-naming': 'fish-name',
  'name-fish': 'fish-name',
  bonk: 'mole-bonk',
  'can-hit': 'can-clatter',
  'coconut-hit': 'coconut-thunk',
  'wood-knock': 'plate-knock',
  knock: 'plate-knock',
  'plate-hit': 'plate-knock',
  clang: 'rim-clang',
  'rim-hit': 'rim-clang',
  'hoop-rim': 'rim-clang',
  'dart-hit': 'dart-thunk',
  machine: 'machine-hum',
  hum: 'machine-hum',
  'pusher-hum': 'machine-hum',
});

/**
 * Resolve any name — canonical, GDD or shorthand — to a registry key.
 * @param {string} name
 * @returns {string|null} Null when nothing implements it.
 */
export function resolve(name) {
  if (!name) return null;
  const n = String(name).toLowerCase();
  if (RECIPES[n]) return n;
  const aliased = ALIASES[n];
  return aliased && RECIPES[aliased] ? aliased : null;
}

/** @param {string} name @returns {{fn, dur, bus, dedupe}|null} */
export function entry(name) {
  const key = resolve(name);
  return key ? RECIPES[key] : null;
}

/** @returns {string[]} Every canonical recipe name, sorted. */
export function recipeNames() {
  return Object.keys(RECIPES).sort();
}

/** @returns {string[]} Every alias, sorted. */
export function aliasNames() {
  return Object.keys(ALIASES).sort();
}

/** @returns {string[]} Materials `impact-hit` understands. */
export function materialNames() {
  return Object.keys(MATERIALS).sort();
}

/**
 * Schedule a recipe into any context — live bus or OfflineAudioContext.
 * @param {BaseAudioContext} ctx
 * @param {AudioNode} dest
 * @param {string} name
 * @param {object} [opts]
 * @param {number} [t0]
 * @returns {number} Absolute end time, or `t0` when the name is unknown.
 */
export function renderInto(ctx, dest, name, opts = {}, t0 = 0) {
  const rec = entry(name);
  if (!rec) return t0;
  return rec.fn(ctx, dest, opts, t0);
}
